/**
 * Template Editor — Phase 3+ (2026-05-21).
 *
 * Dedicated full-page editor for a custom (fork) RA template. Reached via
 * /risk-assessments/library/templates/edit/fork/:id from the Library page.
 *
 * Edits: name, description, activity (4-section), block list (ordered,
 * add/remove/reorder). Row-level hazard editing is NOT part of this surface
 * — that lives on the Block editor, since rows belong to blocks not
 * templates. See plans/hmm-not-sure-we-rustling-mountain.md for the
 * "why not reuse RA detail" reasoning.
 *
 * Save semantics: PUT /api/risk-assessments/templates/fork/:id with the
 * full payload. After the polymorphic refactor (Task 5/5, 2026-05-21) the
 * client sends `blocks: [{ kind, seed_block_id?, ra_block_id?, sort_order }]`
 * instead of `ra_block_ids: [...]`. The server still accepts the legacy
 * shape for backwards-compat.
 *
 * Polymorphic block list (Task 5/5):
 * - Each block from GET /templates/fork/:id carries:
 *     { join_id, kind: 'seed'|'fork', seed_block_id, ra_block_id, name,
 *       seed_slug, sort_order }
 * - `join_id` is the ra_template_blocks row id — the stable identifier we
 *   use for React keys, expand state, reorder ops, and the customise URL.
 * - `kind` distinguishes seed-ref joins (no per-prod fork) from fork-ref
 *   joins (legacy + customised). Detail fetches branch on kind:
 *     kind='seed' → GET /blocks/seed/:seedBlockId
 *     kind='fork' → GET /blocks/fork/:raBlockId
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import remountOnRouteParams from '../utils/remountOnRouteParams';
import { BTN, BTN_XS } from '../constants/buttons';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import ActivityField from '../components/ActivityField';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import BlockRowsTable from '../components/BlockRowsTable';
import HrnInput from '../components/HrnInput';
import ControlsByStage from '../components/ControlsByStage';
import PpePicker from '../components/PpePicker';
import RegsSelector from '../components/RegsSelector';
import RiskReductionBar from '../components/RiskReductionBar';
import OverallRiskFooter from '../components/OverallRiskFooter';
import { computeOverallStats } from '../utils/raStats';
import { computeHrn } from '../constants/riskAssessment';
import { usePageTitle } from '../contexts/PageHeaderContext';

// One instance per template route: three routes render this, and the expanded /
// edited block state and detail cache are keyed by join id, not template (R14 review).
export default remountOnRouteParams(
  RiskAssessmentTemplateEditor,
  (params, props) => `${props.seedMode ? 'seed' : 'prod'}:${params.id || 'new'}`,
);

function RiskAssessmentTemplateEditor({ seedMode = false }) {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const templateId = parseInt(routeId, 10);
  // seedMode: admin-only CrewForge platform-library authoring (writes
  // seed_templates, not ra_templates). No production needed, no fork, no
  // per-prod customise. Uses POST/PUT /seed-templates endpoints.
  // URL conventions:
  //   - /risk-assessments/library/seed-templates/new      → create new seed
  //   - /risk-assessments/library/seed-templates/edit/:id → edit existing seed
  const isNewSeed = seedMode && !routeId;
  const { isAdmin, capabilities } = useDepartment();
  const { user } = useAuth();
  // Template authoring — canAuthorRiskAssessments in the ACTIVE department
  // (server gate requireDepartmentLibraryAuthor; tranche 3 increment 5 — was
  // the unscoped `isBuyer || activeRole === 'supervisor'`).
  const canAuthor = isAdmin || capabilities.canAuthorRiskAssessments === true;

  // Surface the page title in the Layout sticky bar — same pattern as
  // TeamManage / MyTeamTasks / Budget pages. Replaces the inline h1.
  usePageTitle('Template Editor');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Editor state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [activity, setActivity] = useState('');
  // blocks: [{ join_id, kind: 'seed'|'fork', seed_block_id, ra_block_id, name, seed_slug }]
  // - `join_id` is the ra_template_blocks row id — stable across kind, used
  //   for React keys, expand state, reorder + customise.
  // - `kind` mirrors the server's: 'seed' = direct seed_block reference (no
  //   per-prod fork), 'fork' = per-prod ra_block reference (legacy + customised).
  const [blocks, setBlocks] = useState([]);
  const [createdBy, setCreatedBy] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Block picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [allBlocks, setAllBlocks] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Expand state for the block rows — single-expand semantics matching the
  // Library's pattern. Cache holds lazy-loaded detail keyed by join_id so
  // toggling a previously-opened block doesn't refetch. Join_id is stable
  // across both seed-ref and fork-ref joins (it's the ra_template_blocks PK).
  const [expandedJoinId, setExpandedJoinId] = useState(null);
  const [blockDetailCache, setBlockDetailCache] = useState({}); // { [join_id]: { rows, loading, error } }

  // Edit-hazard state — only one block in edit mode at a time. editDraft
  // holds the form values for the currently-being-edited block. On save we
  // POST to the customise endpoint which forks the block + rewires the
  // template's reference. editingJoinId is the join_id (not block id) so
  // it works for both seed-ref and fork-ref joins.
  const [editingJoinId, setEditingJoinId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [customising, setCustomising] = useState(false);

  // Authorisation — server is authoritative, this is just the UX gate.
  const canEdit = canAuthor || createdBy === user?.id;

  // Leaving with unsaved template changes (R14, 2026-09-15 — this page had NO
  // guard of any kind, not even `beforeunload`). `isDirty` is forced true on a
  // BLANK new seed template (it enables Save), so an untouched blank form is
  // excluded here: prompting over nothing trains people to click Leave.
  // ⚠️ An open hazard edit (`editDraft`) is not counted — opening it is not a
  // change and there is no snapshot to compare against; declared, not covered.
  const hasTemplateWork = isDirty
    && (!isNewSeed || name.trim() !== '' || description !== '' || activity !== '' || blocks.length > 0);
  const leaveGuard = useUnsavedChangesGuard({
    when: hasTemplateWork,
    message: "Your changes to this template haven't been saved. If you leave, they will be lost.",
  });

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // New CrewForge seed template — start blank.
      if (isNewSeed) {
        setName('');
        setDescription('');
        setActivity('');
        setBlocks([]);
        setCreatedBy(null);
        setIsDirty(true);
        setLoading(false);
        return;
      }
      // Seed-mode edit: load via /templates/seed/:id (existing read endpoint).
      const endpoint = seedMode
        ? `/api/risk-assessments/templates/seed/${templateId}`
        : `/api/risk-assessments/templates/fork/${templateId}`;
      const res = await apiGet(endpoint);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Template not found or you do not have access to it.');
        if (res.status === 403) throw new Error('You do not have permission to edit this template.');
        throw new Error('Failed to load template');
      }
      const data = await res.json();
      setName(data.name || '');
      setDescription(data.description || '');
      setActivity(data.activity || '');
      // Server returns polymorphic block list (Task 4) — each row has a
      // server-set `kind` ('seed' or 'fork'), a stable `join_id`, plus the
      // appropriate id field for its kind. Mirror that shape verbatim so the
      // UI can branch on kind cleanly.
      //
      // seedMode: GET /templates/seed/:id returns { seed_block_slug,
      // sort_order, seed_block_id, name } per block — no join_id (no
      // ra_template_blocks row), no kind (always seed for CF templates).
      // Synthesise the editor shape so the rest of the UI works unchanged.
      setBlocks((data.blocks || []).map((b, i) => seedMode
        ? {
            join_id: `seed-${b.seed_block_id}-${i}`,  // synthetic key — no real ra_template_blocks row
            kind: 'seed',
            seed_block_id: b.seed_block_id,
            ra_block_id: null,
            name: b.name || 'Unknown block',
            seed_slug: b.seed_block_slug || null,
          }
        : {
            join_id: b.join_id,
            kind: b.kind, // 'seed' | 'fork'
            seed_block_id: b.seed_block_id || null,
            ra_block_id: b.ra_block_id || null,
            name: b.name || 'Unknown block',
            seed_slug: b.seed_slug || null,
          }
      ));
      setCreatedBy(data.created_by || null);
      setIsDirty(false);
    } catch (err) {
      setError(err.message || 'Failed to load template');
    }
    setLoading(false);
  }, [templateId, seedMode, isNewSeed]);

  useEffect(() => { loadContent(); }, [loadContent]);

  // Pre-fetch all block details so the OverallRiskFooter has a complete
  // hazard count without requiring the user to expand each block. Fires
  // whenever blocks[] changes; the cache-presence filter prevents duplicate
  // fetches when a block is re-added or after a save round-trip. Temp
  // (unsaved) rows have negative join_ids and no server detail to fetch.
  useEffect(() => {
    const toFetch = blocks.filter((b) => {
      if (typeof b.join_id === 'number' && b.join_id < 0) return false;
      if (blockDetailCache[b.join_id]) return false;
      if (b.kind === 'seed' && !b.seed_block_id) return false;
      if (b.kind === 'fork' && !b.ra_block_id) return false;
      return true;
    });
    if (toFetch.length === 0) return undefined;
    let cancelled = false;
    Promise.all(toFetch.map(async (b) => {
      const url = b.kind === 'seed'
        ? `/api/risk-assessments/blocks/seed/${b.seed_block_id}`
        : `/api/risk-assessments/blocks/fork/${b.ra_block_id}`;
      try {
        const res = await apiGet(url);
        if (!res.ok) return null;
        const data = await res.json();
        return [b.join_id, { rows: data.rows || [], loading: false, error: null }];
      } catch {
        return null;
      }
    })).then((results) => {
      if (cancelled) return;
      setBlockDetailCache((cur) => {
        const next = { ...cur };
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  // Aggregate residual-HRN stats across every block's rows for the sticky
  // OverallRiskFooter — mirrors RA Detail's per-RA summary, but template-
  // scoped (all blocks rather than all sections). Updates as blocks are
  // pre-fetched above or manually expanded.
  const overallStats = useMemo(() => {
    const allRows = blocks.flatMap((b) => blockDetailCache[b.join_id]?.rows || []);
    return computeOverallStats(allRows);
  }, [blocks, blockDetailCache]);

  // Block picker — lazy-load list when opened the first time.
  const loadPickerBlocks = useCallback(async () => {
    if (allBlocks.length > 0) return; // cache
    setPickerLoading(true);
    try {
      const res = await apiGet('/api/risk-assessments/blocks');
      if (!res.ok) throw new Error('Failed to load blocks');
      const data = await res.json();
      setAllBlocks(Array.isArray(data) ? data : []);
    } catch (err) {
      // Surface inside the picker rather than blowing up the whole page.
      alert(err.message || 'Failed to load blocks');
    }
    setPickerLoading(false);
  }, [allBlocks.length]);

  // Picker filtering — search by name or category. The "already in list"
  // check now needs to consider BOTH the picker-row kind+id AND the saved
  // block kind+id: a seed in the picker matches a seed-ref entry; a fork in
  // the picker matches a fork-ref entry OR a seed-ref entry sourced from
  // the same seed (post-clone the template still references the seed).
  const selectedKeys = useMemo(() => {
    const set = new Set();
    for (const b of blocks) {
      if (b.kind === 'seed' && b.seed_block_id) set.add(`seed:${b.seed_block_id}`);
      if (b.kind === 'fork' && b.ra_block_id) set.add(`fork:${b.ra_block_id}`);
      // Seed-ref entries also dedup against picker seeds with the same
      // underlying seed id, so picking the same seed twice is a no-op.
      if (b.seed_block_id) set.add(`seed:${b.seed_block_id}`);
    }
    return set;
  }, [blocks]);
  const filteredPickerBlocks = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return allBlocks;
    return allBlocks.filter((b) =>
      (b.name || '').toLowerCase().includes(q) ||
      (b.category || '').toLowerCase().includes(q) ||
      (b.seed_slug || '').toLowerCase().includes(q)
    );
  }, [allBlocks, pickerSearch]);

  // Add block — polymorphic. We no longer auto-fork seeds (Task 2 removed
  // the implicit fork). Just append a join row in local state with the
  // appropriate kind/id; the PUT handler writes the polymorphic insert.
  // Client uses a temporary negative join_id so React keys remain unique
  // for new-but-unsaved rows (collapsed back to real ids on the next GET).
  const addBlock = (pickerBlock) => {
    if (adding) return;
    const isSeed = pickerBlock.kind === 'seed';
    const dedupKey = isSeed
      ? `seed:${pickerBlock.id}`
      : `fork:${pickerBlock.id}`;
    if (selectedKeys.has(dedupKey)) {
      setPickerOpen(false);
      setPickerSearch('');
      return;
    }
    setAdding(true);
    try {
      // Negative pseudo join_id for unsaved rows — distinct per add.
      const tempJoinId = -Date.now() - Math.floor(Math.random() * 1000);
      const newEntry = isSeed
        ? {
            join_id: tempJoinId,
            kind: 'seed',
            seed_block_id: pickerBlock.id,
            ra_block_id: null,
            name: pickerBlock.name,
            seed_slug: pickerBlock.seed_slug || null,
          }
        : {
            join_id: tempJoinId,
            kind: 'fork',
            seed_block_id: pickerBlock.seed_block_id || null,
            ra_block_id: pickerBlock.id,
            name: pickerBlock.name,
            seed_slug: pickerBlock.seed_slug || null,
          };
      setBlocks((prev) => [...prev, newEntry]);
      setIsDirty(true);
      setPickerOpen(false);
      setPickerSearch('');
    } catch (err) {
      alert(err.message || 'Failed to add block');
    }
    setAdding(false);
  };

  const removeBlock = (joinId) => {
    setBlocks((prev) => prev.filter((b) => b.join_id !== joinId));
    setIsDirty(true);
  };

  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[target]] = [next[target], next[idx]];
    setBlocks(next);
    setIsDirty(true);
  };

  // URL for fetching block detail — branches on kind. Seed-ref rows hit the
  // seed-detail endpoint; fork-ref rows hit the fork-detail endpoint. Both
  // return the same `{ rows: [...] }` shape so BlockRowsTable renders unchanged.
  const blockDetailUrl = (block) => (block.kind === 'seed'
    ? `/api/risk-assessments/blocks/seed/${block.seed_block_id}`
    : `/api/risk-assessments/blocks/fork/${block.ra_block_id}`);

  const toggleBlockExpansion = async (block) => {
    const joinId = block.join_id;
    // Collapse if it's the currently-expanded one.
    if (expandedJoinId === joinId) {
      setExpandedJoinId(null);
      return;
    }
    setExpandedJoinId(joinId);
    // Unsaved (temp) rows have no server detail to fetch yet — bail early.
    if (joinId < 0) return;
    // Lazy-load + cache the block's hazard rows, keyed by join_id.
    if (!blockDetailCache[joinId]) {
      setBlockDetailCache((prev) => ({ ...prev, [joinId]: { rows: null, loading: true, error: null } }));
      try {
        const res = await apiGet(blockDetailUrl(block));
        if (!res.ok) throw new Error('Failed to load block');
        const data = await res.json();
        setBlockDetailCache((prev) => ({
          ...prev,
          [joinId]: { rows: data.rows || [], loading: false, error: null },
        }));
      } catch (err) {
        setBlockDetailCache((prev) => ({
          ...prev,
          [joinId]: { rows: null, loading: false, error: err.message || 'Failed to load block' },
        }));
      }
    }
  };

  const handleSave = async () => {
    if (saving || (!isDirty && !isNewSeed)) return;
    if (!name.trim()) {
      alert('Template name cannot be empty.');
      return;
    }
    // Safety prompt before writing to the platform-wide CrewForge library.
    // Guards against the "I forgot I'm logged in as admin" footgun — these
    // changes ripple out to every user on every production immediately.
    if (seedMode) {
      const intro = isNewSeed
        ? 'This new CrewForge template will be visible to EVERY user on EVERY production as soon as it saves.'
        : 'These changes to a CrewForge template will be visible to EVERY user on EVERY production as soon as they save.';
      if (!window.confirm(
        `⚠ Editing the platform-wide CrewForge Library Template\n\n${intro}\n\n`
        + 'If you only meant to change your own template, cancel and switch '
        + 'to an individual user account first.\n\nContinue saving?'
      )) {
        return;
      }
    }
    setSaving(true);
    try {
      // Seed-mode (admin CrewForge platform-library) — flat seed_block_ids.
      // CrewForge templates can only reference seed_blocks (not per-prod
      // forks), so filter out any fork-kind blocks defensively.
      if (seedMode) {
        const seedBlockIds = blocks
          .filter((b) => b.kind === 'seed' && b.seed_block_id)
          .map((b) => b.seed_block_id);
        const body = {
          name: name.trim(),
          description,
          activity,
          seed_block_ids: seedBlockIds,
        };
        if (isNewSeed) {
          const res = await apiPost('/api/risk-assessments/seed-templates', body);
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to create CrewForge default template');
          }
          const data = await res.json();
          setIsDirty(false);
          leaveGuard.allowNextNavigation(); // the flag above has not re-rendered yet
          navigate(`/risk-assessments/library/seed-templates/edit/${data.id}`, { replace: true });
        } else {
          const res = await apiPut(`/api/risk-assessments/seed-templates/${templateId}`, body);
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to save CrewForge default template');
          }
          setIsDirty(false);
          await loadContent();
        }
      } else {
        // Per-prod fork — polymorphic PUT body (kind + relevant id per block).
        // Sort_order is implicit (array order). The server validates the
        // polymorphic invariant (exactly one of seed_block_id/ra_block_id set).
        const polyBlocks = blocks.map((b, i) => (b.kind === 'seed'
          ? { kind: 'seed', seed_block_id: b.seed_block_id, sort_order: i }
          : { kind: 'fork', ra_block_id: b.ra_block_id, sort_order: i }));
        const res = await apiPut(`/api/risk-assessments/templates/fork/${templateId}`, {
          name: name.trim(),
          description,
          activity,
          blocks: polyBlocks,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to save template');
        }
        // Reset dirty BEFORE the refetch (loadContent doesn't touch isDirty;
        // the seed branch above already does this, fork branch was missing it).
        // Without this, the save-bar stays at "● Unsaved changes" after a
        // successful save until the user explicitly mutates a field.
        setIsDirty(false);
        // Refetch so any server-side normalisation (e.g. trimmed name) is reflected.
        await loadContent();
      }
    } catch (err) {
      alert(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!isDirty) return;
    if (!window.confirm('Discard your unsaved changes to this template?')) return;
    await loadContent();
  };

  // ── Edit-hazard (auto-fork) handlers ───────────────────────────────

  const enterEditMode = async (block) => {
    // Unsaved (temp) join rows can't be customised yet — would need a real
    // server-side join row to swap. Gently nudge the user to save first.
    if (block.join_id < 0) {
      alert('Save the template first, then you can edit this hazard.');
      return;
    }
    // Lazy-load the block's detail if it's not already in the cache (the
    // user may click Edit on a row they haven't expanded yet). Branches on
    // kind for the fetch URL.
    let detail = blockDetailCache[block.join_id];
    if (!detail || !detail.rows) {
      try {
        const res = await apiGet(blockDetailUrl(block));
        if (!res.ok) throw new Error('Failed to load block');
        const data = await res.json();
        detail = { rows: data.rows || [], loading: false, error: null };
        setBlockDetailCache((prev) => ({ ...prev, [block.join_id]: detail }));
      } catch (err) {
        alert(err.message || 'Failed to load block');
        return;
      }
    }
    if (!detail.rows || detail.rows.length === 0) return;
    const row = detail.rows[0];
    // Auto-expand the block so the editor renders in place.
    setExpandedJoinId(block.join_id);
    setEditingJoinId(block.join_id);
    setEditDraft({
      name: `${block.name} (custom)`,
      hazard: row.hazard || '',
      who_affected: row.who_affected || '',
      lo_init: row.lo_init,
      fe_init: row.fe_init,
      dph_init: row.dph_init,
      np_init: row.np_init,
      controls: row.controls || '',
      lo_res: row.lo_res,
      fe_res: row.fe_res,
      dph_res: row.dph_res,
      np_res: row.np_res,
      notes: row.notes || '',
      ppe_list: row.ppe_list || '[]',
      regs_list: row.regs_list || '[]',
    });
  };

  const cancelEdit = () => {
    setEditingJoinId(null);
    setEditDraft(null);
  };

  const updateDraft = (patch) => {
    setEditDraft((prev) => ({ ...prev, ...patch }));
  };

  const saveCustomise = async () => {
    if (!editingJoinId || !editDraft || customising) return;
    if (!editDraft.name.trim()) {
      alert('Block name cannot be empty.');
      return;
    }
    setCustomising(true);
    try {
      // New polymorphic URL (Task 3): identifies the join row, works for
      // both seed-ref and fork-ref joins. Server swaps the join to point at
      // a freshly-forked ra_block created from the edited draft.
      const res = await apiPost(
        `/api/risk-assessments/templates/fork/${templateId}/join/${editingJoinId}/customise`,
        editDraft,
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to customise block');
      }
      // Clear cache for this join — the underlying block content has been
      // replaced. The join_id is preserved across customise: the server only
      // swaps ra_block_id ↔ seed_block_id in the same ra_template_blocks row
      // (see riskAssessments.js around the UPDATE in customiseTemplateBlock).
      // We refetch below to pick up the new block's name and content, then
      // re-expand the same join_id (which still exists).
      setBlockDetailCache((prev) => {
        const next = { ...prev };
        delete next[editingJoinId];
        return next;
      });
      const oldJoinId = editingJoinId;
      setEditingJoinId(null);
      setEditDraft(null);
      // Refetch the template so the block list shows the new bespoke block.
      await loadContent();
      // Re-expand the same join (its id is unchanged — the server UPDATEd
      // the existing ra_template_blocks row to point at the new ra_block).
      setExpandedJoinId(oldJoinId);
    } catch (err) {
      alert(err.message || 'Failed to customise block');
    }
    setCustomising(false);
  };

  const handleDelete = async () => {
    // seedMode delete = DELETE /seed-templates/:id (writes seed_templates).
    // Per-prod fork delete = DELETE /templates/fork/:id (writes ra_templates).
    // Strong warning wording in seedMode matches the save flow — same footgun
    // (admin forgets they're not on their own account, takes the whole library
    // down with one click).
    const confirmMsg = seedMode
      ? `⚠ Deleting from the platform-wide CrewForge Library Template\n\n`
        + `The CrewForge template "${name}" will disappear from EVERY user's library on EVERY production on next page load. Existing RAs created from this template are unaffected.\n\n`
        + `If you only meant to delete your own template, cancel and switch to an individual user account first.\n\nContinue?`
      : `Delete the template "${name}"? This cannot be undone. Existing RAs created from this template are unaffected.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const endpoint = seedMode
        ? `/api/risk-assessments/seed-templates/${templateId}`
        : `/api/risk-assessments/templates/fork/${templateId}`;
      const res = await apiDelete(endpoint);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete template');
      }
      leaveGuard.allowNextNavigation(); // deleted — nothing left to save
      navigate('/risk-assessments/library');
    } catch (err) {
      alert(err.message || 'Failed to delete template');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full mx-auto">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full mx-auto">
        <ErrorMessage error={error} />
        <div className="mt-3">
          <button
            onClick={() => navigate('/risk-assessments/library')}
            className={BTN_XS.secondary}
          >
            Back to library
          </button>
        </div>
      </div>
    );
  }

  const inputClass = 'block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-70';
  const labelClass = 'block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1';

  const cardClass = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6';
  const cardHeadingClass = 'text-base font-semibold text-gray-800 dark:text-gray-200';
  const captionClass = 'text-[11px] text-gray-500 dark:text-gray-400 mt-1';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full mx-auto pb-32" data-testid="ra-template-editor">
      {leaveGuard.dialog}
      {/* Breadcrumb — title now lives in the Layout sticky bar via usePageTitle. */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <button
          onClick={() => navigate('/risk-assessments/library')}
          className="text-xs text-indigo-700 dark:text-indigo-300 hover:underline"
        >
          ← Back to library
        </button>
      </div>

      <div className="space-y-4 md:space-y-6">

        {/* ── Library Details card ───────────────────────────────────── */}
        <section className={cardClass} aria-labelledby="card-library-details">
          <header className="mb-4">
            <h2 id="card-library-details" className={cardHeadingClass}>Library Details</h2>
            <p className={captionClass}>How this template appears when users browse the library.</p>
          </header>

          <label className="block mb-4">
            <span className={labelClass}>Template name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
              disabled={!canEdit || saving}
              maxLength={200}
              className={inputClass}
              data-testid="ra-template-editor-name"
            />
          </label>

          <label className="block">
            <span className={labelClass}>Template description</span>
            <input
              type="text"
              value={description}
              onChange={(e) => { setDescription(e.target.value); setIsDirty(true); }}
              disabled={!canEdit || saving}
              maxLength={1000}
              placeholder="Short summary visible in the template list…"
              className={inputClass}
              data-testid="ra-template-editor-description"
            />
          </label>
        </section>

        {/* ── Activity description card ──────────────────────────────── */}
        <section className={cardClass} aria-labelledby="card-activity">
          <header className="mb-4">
            <h2 id="card-activity" className={cardHeadingClass}>Activity description</h2>
            <p className={captionClass}>
              Auto-populates the Activity field when a user creates an RA from this template.
              These four sub-sections (name, description, equipment, methodology) appear on the
              materialised RA — not on this template's library card.
            </p>
          </header>
          <ActivityField
            value={activity}
            canEdit={canEdit && !saving}
            onSave={(next) => setActivity(next)}
            onDirty={() => setIsDirty(true)}
          />
        </section>

        {/* ── Hazard blocks card ─────────────────────────────────────── */}
        <section className={cardClass} aria-labelledby="card-hazards">
          <header className="mb-4 flex items-center justify-between gap-2 flex-wrap">
            <h2 id="card-hazards" className={cardHeadingClass}>
              Hazard blocks
              <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">{blocks.length}</span>
            </h2>
          </header>
        {blocks.length === 0 ? (
          <p className="py-3 text-sm text-gray-500 dark:text-gray-400 italic">
            No blocks yet. Click + Add block to compose this template.
          </p>
        ) : (
          <ol className="space-y-2">
            {blocks.map((b, i) => {
              const isExpanded = expandedJoinId === b.join_id;
              const detail = blockDetailCache[b.join_id];
              // Badge rule (post-polymorphic):
              // - kind='seed' → always a CrewForge default (seed-ref)
              // - kind='fork' with seed_slug set → fork-of-seed (still a CF default)
              // - kind='fork' without seed_slug → bespoke / Custom
              const isCfDefault = b.kind === 'seed' || (b.kind === 'fork' && !!b.seed_slug);
              const isBespoke = b.kind === 'fork' && !b.seed_slug;
              // Stable testid suffix for e2e tests — preserves the historical
              // ra_block_id-based selector when present, falls back to join_id
              // for seed-ref rows. Tests that targeted ra_block_id continue
              // working for fork-ref entries.
              const testIdSuffix = b.ra_block_id != null ? `${b.ra_block_id}` : `join-${b.join_id}`;
              // Block-level worst-of summary for the inline RiskReductionBar
              // in the header. Mirrors RA Detail's collapsed-hazard pattern:
              // bar visible in both collapsed AND expanded states. We use
              // max-initial / max-residual independently across the block's
              // rows so the bar represents the worst journey the block
              // contains — consistent with the OverallRiskFooter semantics.
              let blockWorstInit = 0;
              let blockWorstRes = 0;
              if (detail?.rows?.length) {
                for (const r of detail.rows) {
                  const hi = computeHrn(r.lo_init, r.fe_init, r.dph_init, r.np_init);
                  const hr = computeHrn(r.lo_res, r.fe_res, r.dph_res, r.np_res);
                  if (hi > blockWorstInit) blockWorstInit = hi;
                  if (hr > blockWorstRes) blockWorstRes = hr;
                }
              }
              const hasBlockBar = detail?.rows?.length > 0;
              return (
                <li
                  key={b.join_id}
                  className={`rounded-md border overflow-hidden transition-colors ${
                    isExpanded
                      ? 'border-gray-300 dark:border-slate-500 bg-gray-50 dark:bg-slate-700/40'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  data-testid={`ra-template-editor-block-${testIdSuffix}`}
                >
                  <div className={`flex items-center gap-2 px-3 py-2 flex-wrap transition-colors ${isExpanded ? 'hover:bg-gray-100 dark:hover:bg-slate-700/60' : ''}`}>
                    <span className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300">
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleBlockExpansion(b)}
                      className="min-w-0 flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                      data-testid={`ra-template-editor-block-toggle-${testIdSuffix}`}
                    >
                      <span className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate" title={b.name}>{b.name}</span>
                      {isCfDefault && (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                          CF
                        </span>
                      )}
                      {isBespoke && (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          Custom
                        </span>
                      )}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`shrink-0 h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {/* Inline block-level risk bar — worst initial → worst
                        residual across this block's hazards. Same placement
                        rule as RA Detail's collapsed-hazard bar (between
                        title/toggle and right-aligned actions). */}
                    {hasBlockBar && (
                      <div
                        className="flex-1 min-w-[160px] -mt-3 cursor-pointer"
                        onClick={() => toggleBlockExpansion(b)}
                        title="Worst initial → worst residual across this block's hazards"
                      >
                        <RiskReductionBar initialHrn={blockWorstInit} residualHrn={blockWorstRes} />
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => moveBlock(i, -1)}
                          disabled={i === 0 || saving}
                          className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`ra-template-editor-block-up-${testIdSuffix}`}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBlock(i, 1)}
                          disabled={i === blocks.length - 1 || saving}
                          className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`ra-template-editor-block-down-${testIdSuffix}`}
                        >
                          ↓
                        </button>
                        {/* Per-block hazard customise hits the per-prod
                            customise endpoint (forks the underlying block);
                            doesn't apply in seedMode where we're editing the
                            CrewForge default itself. To change a seed block,
                            admin uses the "Edit CrewForge default" button on
                            the BLOCK row in the Library. */}
                        {!seedMode && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); enterEditMode(b); }}
                            disabled={saving || customising}
                            className="px-2 py-1 text-xs rounded border border-indigo-200 dark:border-indigo-800/60 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40"
                            title="Edit this hazard — saves as a new custom block"
                            data-testid={`ra-template-editor-block-edit-${testIdSuffix}`}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeBlock(b.join_id)}
                          disabled={saving}
                          className="px-2 py-1 text-xs rounded border border-red-200 dark:border-red-800/60 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                          title="Remove from template"
                          data-testid={`ra-template-editor-block-remove-${testIdSuffix}`}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-gray-700" data-testid={`ra-template-editor-block-detail-${testIdSuffix}`}>
                      {b.join_id < 0 ? (
                        <p className="py-2 text-sm text-gray-500 dark:text-gray-400 italic">
                          New block — save the template to load its content.
                        </p>
                      ) : !detail || detail.loading ? (
                        <div className="py-3"><LoadingSpinner /></div>
                      ) : detail.error ? (
                        <p className="py-2 text-sm text-red-600 dark:text-red-400">{detail.error}</p>
                      ) : editingJoinId === b.join_id && editDraft ? (
                        <BlockEditForm
                          draft={editDraft}
                          updateDraft={updateDraft}
                          inputClass={inputClass}
                          labelClass={labelClass}
                          onSave={saveCustomise}
                          onCancel={cancelEdit}
                          saving={customising}
                          testIdPrefix={`ra-template-editor-edit-${testIdSuffix}`}
                        />
                      ) : (
                        <BlockRowsTable rows={detail.rows} />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* Add block button + inline picker */}
        {canEdit && !pickerOpen && (
          <button
            type="button"
            onClick={() => { setPickerOpen(true); loadPickerBlocks(); }}
            disabled={saving}
            className="mt-3 w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            data-testid="ra-template-editor-add-block"
          >
            + Add block
          </button>
        )}

        {pickerOpen && (
          <div className="mt-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3" data-testid="ra-template-editor-picker">
            <div className="flex items-center justify-between mb-2 gap-2">
              <input
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search hazard blocks by name or category…"
                className={`${inputClass} flex-1`}
                autoFocus
                data-testid="ra-template-editor-picker-search"
              />
              <button
                type="button"
                onClick={() => { setPickerOpen(false); setPickerSearch(''); }}
                className={BTN_XS.secondary}
              >
                Cancel
              </button>
            </div>
            {pickerLoading ? (
              <div className="py-4"><LoadingSpinner /></div>
            ) : (
              <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredPickerBlocks.length === 0 ? (
                  <li className="py-3 text-sm text-gray-500 dark:text-gray-400 italic">
                    No blocks match that search.
                  </li>
                ) : (
                  filteredPickerBlocks.map((b) => {
                    // Already-added when the picker row matches an existing
                    // join: forks by ra_block_id, seeds by seed_block_id.
                    const alreadyAdded = b.kind === 'fork'
                      ? selectedKeys.has(`fork:${b.id}`)
                      : selectedKeys.has(`seed:${b.id}`);
                    return (
                      <li key={`${b.kind}:${b.id}`} className="flex items-center gap-2 py-2">
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate" title={b.name}>{b.name}</span>
                          {(b.kind === 'seed' || (b.kind === 'fork' && b.seed_slug)) && (
                            <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                              CF
                            </span>
                          )}
                          {b.kind === 'fork' && !b.seed_slug && (
                            <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                              Custom
                            </span>
                          )}
                          {b.category && (
                            <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500 truncate">
                              · {b.category}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => addBlock(b)}
                          disabled={adding || alreadyAdded}
                          className={`${BTN_XS.primary} shrink-0`}
                          data-testid={`ra-template-editor-picker-add-${b.kind}-${b.id}`}
                        >
                          {alreadyAdded ? 'Added' : adding ? 'Adding…' : 'Add'}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        )}
        </section>

        {/* ── Danger zone card — admin can delete CrewForge defaults in
              seedMode too (DELETE /seed-templates/:id, added 2026-05-23).
              The Delete button's handler branches inside handleDelete. */}
        {canEdit && (
          <section
            className="mt-8 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/40 dark:bg-red-900/10 p-4 sm:p-6"
            aria-labelledby="card-danger"
          >
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className={`${BTN['danger-soft']} shrink-0`}
                data-testid="ra-template-editor-delete"
              >
                Delete template
              </button>
              <p className="text-[11px] text-red-700/70 dark:text-red-300/70 flex-1 min-w-0">
                Soft-delete this template. Existing RAs created from it are unaffected; the
                template just disappears from the library. Cannot be undone from the UI.
              </p>
            </div>
          </section>
        )}

      </div>

      {/* Sticky save bar — mirrors the RA detail save-bar pattern. z-30 so
          the cookie banner (z-50) still wins if ever shown, but normal page
          content sits below it. */}
      {canEdit && (
        <div
          className="fixed bottom-0 left-0 right-0 sm:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 z-30"
          data-testid="ra-template-editor-save-bar"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              {isDirty ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  ● Unsaved changes
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">All saved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isDirty && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className={BTN.secondary}
                  data-testid="ra-template-editor-cancel"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saving}
                className={BTN.confirm}
                data-testid="ra-template-editor-save"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Worst-residual summary footer — mirrors RA Detail. pushUp lifts it
          above the save bar when one is rendered (canEdit). */}
      <OverallRiskFooter stats={overallStats} pushUp={canEdit} />

    </div>
  );
}

// ── BlockEditForm ────────────────────────────────────────────────────
// Inline edit-hazard form shown inside the expanded block panel. Saving
// triggers the customise endpoint which forks the underlying block into
// a new bespoke ra_block + rewires the template's reference.
function BlockEditForm({ draft, updateDraft, inputClass, labelClass, onSave, onCancel, saving, testIdPrefix }) {
  const initialHrn = (Number(draft.lo_init) || 0)
    * (Number(draft.fe_init) || 0)
    * (Number(draft.dph_init) || 0)
    * (Number(draft.np_init) || 0);
  return (
    <div className="space-y-4">
      {/* Info banner — explain the auto-fork semantics so the user isn't
          surprised by a new entry in the library. */}
      <div
        className="rounded-md border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 px-3 py-2 text-xs leading-relaxed"
        data-testid={`${testIdPrefix}-banner`}
      >
        Saving will create a new <strong>custom block</strong> in your library and
        update this template to reference it. The original block stays untouched.
        Existing risk assessments are unaffected.
      </div>

      <label className="block">
        <span className={labelClass}>Block name</span>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => updateDraft({ name: e.target.value })}
          disabled={saving}
          maxLength={200}
          className={inputClass}
          data-testid={`${testIdPrefix}-name`}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={labelClass}>Hazard</span>
          <input
            type="text"
            value={draft.hazard}
            onChange={(e) => updateDraft({ hazard: e.target.value })}
            disabled={saving}
            maxLength={1000}
            className={inputClass}
            data-testid={`${testIdPrefix}-hazard`}
          />
        </label>
        <label className="block">
          <span className={labelClass}>Who is affected</span>
          <input
            type="text"
            value={draft.who_affected}
            onChange={(e) => updateDraft({ who_affected: e.target.value })}
            disabled={saving}
            maxLength={500}
            className={inputClass}
            placeholder="e.g. Operator, crew nearby, public"
            data-testid={`${testIdPrefix}-who`}
          />
        </label>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <span className={labelClass}>Initial HRN (before controls)</span>
          <HrnInput
            values={{ lo: draft.lo_init, fe: draft.fe_init, dph: draft.dph_init, np: draft.np_init }}
            onChange={(next) => updateDraft({
              lo_init: next.lo, fe_init: next.fe, dph_init: next.dph, np_init: next.np,
            })}
            disabled={saving}
            testIdPrefix={`${testIdPrefix}-hrn-init`}
          />
        </div>
        <div>
          <span className={labelClass}>Residual HRN (after controls)</span>
          <HrnInput
            values={{ lo: draft.lo_res, fe: draft.fe_res, dph: draft.dph_res, np: draft.np_res }}
            comparedTo={initialHrn}
            onChange={(next) => updateDraft({
              lo_res: next.lo, fe_res: next.fe, dph_res: next.dph, np_res: next.np,
            })}
            disabled={saving}
            testIdPrefix={`${testIdPrefix}-hrn-res`}
          />
        </div>
      </div>

      <div>
        <span className={labelClass}>Control measures</span>
        <ControlsByStage
          value={draft.controls}
          onChange={(next) => updateDraft({ controls: next })}
          onCommit={(next) => updateDraft({ controls: next })}
          disabled={saving}
          compact
        />
      </div>

      <div>
        <span className={labelClass}>PPE required</span>
        <PpePicker
          value={draft.ppe_list}
          onChange={(next) => updateDraft({ ppe_list: next })}
          disabled={saving}
        />
      </div>

      <div>
        <span className={labelClass}>Applicable regulations</span>
        <RegsSelector
          value={draft.regs_list}
          onChange={(next) => updateDraft({ regs_list: next })}
          disabled={saving}
        />
      </div>

      <label className="block">
        <span className={labelClass}>Notes</span>
        <input
          type="text"
          value={draft.notes}
          onChange={(e) => updateDraft({ notes: e.target.value })}
          disabled={saving}
          maxLength={1000}
          className={inputClass}
          data-testid={`${testIdPrefix}-notes`}
        />
      </label>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className={BTN.secondary}
          data-testid={`${testIdPrefix}-cancel`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !draft.name.trim()}
          className={BTN.confirm}
          data-testid={`${testIdPrefix}-save`}
        >
          {saving ? 'Saving…' : 'Save as custom block'}
        </button>
      </div>
    </div>
  );
}
